import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, X, Bell } from 'lucide-react';

interface Announcement { id: string; title: string; content: string; priority: string; expiresAt: string; active: boolean; createdAt: string; }
interface Props { villageId: string; villageName: string; }

const PRIORITIES = [
  { value: 'LOW',    label: '🔵 Low',    color: 'text-blue-400 bg-blue-900/30' },
  { value: 'NORMAL', label: '🟢 Normal', color: 'text-green-400 bg-green-900/30' },
  { value: 'HIGH',   label: '🟡 High',   color: 'text-yellow-400 bg-yellow-900/30' },
  { value: 'URGENT', label: '🔴 Urgent', color: 'text-red-400 bg-red-900/30' },
];
const EMPTY_ANN = { title: '', content: '', priority: 'NORMAL', expiresAt: '' };

const CommunityAnnouncementManager: React.FC<Props> = ({ villageId, villageName }) => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState({ ...EMPTY_ANN });
  const [error, setError]       = useState('');

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const r = await fetch(`/api/community-portals/${villageId}/announcements`);
      const d = await r.json();
      if (d.success) setAnnouncements(d.announcements || []);
    } catch { setError('Failed to load announcements'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAnnouncements(); }, [villageId]);

  const cancel = () => { setShowForm(false); setForm({ ...EMPTY_ANN }); setError(''); };

  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) { setError('Title and content are required.'); return; }
    setSaving(true); setError('');
    try {
      const r = await fetch(`/api/community-portals/${villageId}/announcements`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, active: true }),
      });
      const d = await r.json();
      if (d.success) { await fetchAnnouncements(); cancel(); } else setError(d.error || 'Save failed');
    } catch { setError('Network error'); }
    finally { setSaving(false); }
  };

  const deleteAnn = async (id: string, title: string) => {
    if (!confirm(`Delete announcement: "${title}"?`)) return;
    try {
      const r = await fetch(`/api/community-portals/${villageId}/announcements/${id}`, { method: 'DELETE' });
      const d = await r.json();
      if (d.success) await fetchAnnouncements(); else setError(d.error || 'Delete failed');
    } catch { setError('Network error'); }
  };

  const priorityStyle = (p: string) => PRIORITIES.find(x => x.value === p)?.color ?? 'text-slate-400 bg-slate-700/30';
  const priorityLabel = (p: string) => PRIORITIES.find(x => x.value === p)?.label ?? p;
  const isExpired     = (ann: Announcement) => !!ann.expiresAt && new Date(ann.expiresAt) < new Date();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Announcements</h3>
          <p className="text-sm text-slate-400">{villageName} · {announcements.length} announcement{announcements.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => { setShowForm(true); setError(''); }}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[#D4AF37] text-slate-900 hover:bg-[#c49b2f] transition-colors">
          <Plus className="w-4 h-4" /> New Announcement
        </button>
      </div>

      {error && <div className="bg-red-900/30 border border-red-700 rounded-lg p-3 text-red-300 text-sm">{error}</div>}

      {showForm && (
        <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-5 space-y-4">
          <h4 className="font-semibold text-[#D4AF37]">➕ New Announcement</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-sm text-slate-300 mb-1 block">Title *</label>
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Announcement title..."
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
            </div>
            <div>
              <label className="text-sm text-slate-300 mb-1 block">Priority</label>
              <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]">
                {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm text-slate-300 mb-1 block">Expires At (optional)</label>
              <input type="datetime-local" value={form.expiresAt}
                onChange={e => setForm(f => ({ ...f, expiresAt: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm text-slate-300 mb-1 block">Content *</label>
              <textarea rows={4} value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                placeholder="Announcement details..."
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37] resize-y" />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={save} disabled={saving}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium bg-[#0F5132] text-white hover:bg-[#0d4429] disabled:opacity-50 transition-colors">
              <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Post Announcement'}
            </button>
            <button onClick={cancel}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm border border-slate-600 text-slate-300 hover:bg-slate-700 transition-colors">
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-slate-400 text-sm py-8 text-center">Loading announcements…</div>
      ) : announcements.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-slate-700 rounded-xl">
          <Bell className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">No announcements yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {announcements.map(ann => (
            <div key={ann.id}
              className={`bg-slate-800/40 border rounded-xl p-4 transition-colors ${isExpired(ann) ? 'border-slate-700/50 opacity-60' : 'border-slate-700 hover:border-slate-600'}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${priorityStyle(ann.priority)}`}>
                      {priorityLabel(ann.priority)}
                    </span>
                    {isExpired(ann) && <span className="text-xs px-2 py-0.5 rounded-full text-slate-500 bg-slate-700/50">Expired</span>}
                  </div>
                  <p className="text-white font-medium text-sm">{ann.title}</p>
                  <p className="text-slate-400 text-xs mt-1 line-clamp-2">{ann.content}</p>
                  {ann.expiresAt && (
                    <p className="text-slate-500 text-xs mt-1">
                      Expires: {new Date(ann.expiresAt).toLocaleDateString('en-GB', {
                        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                      })}
                    </p>
                  )}
                </div>
                <button onClick={() => deleteAnn(ann.id, ann.title)}
                  className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 hover:bg-slate-700 transition-colors flex-shrink-0">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
export default CommunityAnnouncementManager;
